import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Toolbar,
  Typography,
  useMediaQuery,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTheme } from '@mui/material/styles';
import ChatInterface from 'components/ChatInterface';

export default function AIChat({ drawerOpen, setDrawerOpen }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [chats, setChats] = useState([]);
  const [activeChatId, setActiveChatId] = useState(null);
  const [newChatId, setNewChatId] = useState(null);
  const [newChatTitle, setNewChatTitle] = useState('New chat');
  const chatInterfaceRef = useRef(null);

  const activeChat = chats.find((chat) => chat.id === activeChatId);
  const messages = activeChat?.messages || [];

  useEffect(() => {
    requestAnimationFrame(() => chatInterfaceRef.current?.focus());
  }, []);

  const startChat = () => {
    setActiveChatId(null);
    setNewChatId(null);
    setNewChatTitle('New chat');
    chatInterfaceRef.current?.clear();
    if (isMobile) setDrawerOpen(false);
    requestAnimationFrame(() => chatInterfaceRef.current?.focus());
  };

  const saveMessages = (nextMessages, chatId) => {
    setChats((currentChats) => {
      const id = chatId || `${Date.now()}`;
      const existingChat = currentChats.find((chat) => chat.id === id);
      if (!existingChat) {
        setActiveChatId(id);
        setNewChatId(id);
        setNewChatTitle(nextMessages.find((message) => message.role === 'user')?.text || 'New chat');
        return [...currentChats, { id, title: nextMessages[0]?.text || 'New chat', messages: nextMessages, interactionId: null }];
      }
      return currentChats.map((chat) => chat.id === id ? { ...chat, messages: nextMessages } : chat);
    });
  };

  const saveInteractionId = (value, chatId) => {
    if (!chatId) return;
    setChats((currentChats) => currentChats.map((chat) => (
      chat.id === chatId ? { ...chat, interactionId: value } : chat
    )));
  };

  const chatList = (
    <>
      <ListItemButton
        selected={activeChatId === null || activeChatId === newChatId}
        onClick={() => {
          if (newChatId) {
            setActiveChatId(newChatId);
            if (isMobile) setDrawerOpen(false);
            requestAnimationFrame(() => chatInterfaceRef.current?.focus());
          } else {
            startChat();
          }
        }}
        sx={{ borderRadius: 1, mx: 1, width: 'auto' }}
      >
        <ListItemText primary={newChatTitle} primaryTypographyProps={{ noWrap: true }} />
      </ListItemButton>
      {chats.filter((chat) => chat.id !== newChatId).map((chat) => (
        <ListItemButton
          key={chat.id}
          selected={chat.id === activeChatId}
          onClick={() => {
            setActiveChatId(chat.id);
            if (isMobile) setDrawerOpen(false);
            requestAnimationFrame(() => chatInterfaceRef.current?.focus());
          }}
          sx={{ borderRadius: 1, mx: 1, width: 'auto' }}
        >
          <ListItemText primary={chat.title} primaryTypographyProps={{ noWrap: true }} />
        </ListItemButton>
      ))}
    </>
  );

  return (
    <>
      <Box
        component="main"
        sx={{
          display: 'flex',
          width: drawerOpen && !isMobile ? 'calc(100% - 280px)' : '100%',
          height: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0, width: '100%', display: 'flex', flexDirection: 'column', px: { xs: 1, sm: 2 }, boxSizing: 'border-box' }}>
          <ChatInterface
            ref={chatInterfaceRef}
            chatId={activeChatId}
            messages={messages}
            onMessagesChange={saveMessages}
            interactionId={activeChat?.interactionId}
            onInteractionId={saveInteractionId}
          />
        </Box>
      </Box>
      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        open={drawerOpen}
        ModalProps={isMobile ? { keepMounted: true, onClose: () => setDrawerOpen(false) } : {}}
        sx={{
          width: 280,
          flexShrink: 0,
          '& .MuiDrawer-paper': { width: 280, boxSizing: 'border-box' },
        }}
        anchor="right"
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto', flexGrow: 1}}>
          <Box sx={{ px: 2, py: 1.5 }}>
            <Button
              onClick={startChat}
              aria-label="New chat"
              color="primary"
              variant="contained"
              startIcon={<AddIcon />}
              fullWidth
            >
              New chat
            </Button>
          </Box>
          <Divider />
          <List>{chatList}</List>
          <Typography color="textSecondary" variant="caption" sx={{ textAlign: 'center', py: 1, px: 2, display: 'block' }}>
            Chat histories will be cleared when you refresh the page.
          </Typography>
        </Box>
        <Divider />
        <Box sx={{ p: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <IconButton onClick={() => setDrawerOpen(false)} aria-label="Close drawer">
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </Drawer>
    </>
  );
}
